/**
 * Client connection to a game room.
 *
 * Kate's family rule is why this matters beyond plumbing:
 *
 *   "Humans can take care of dogs and cats. You can be their pets. And when you play
 *    multiplayer, if you're a human and someone's a dog, you can be a family and go out
 *    together."
 *
 * A family needs two real players, so it can only exist over a connection. Offline the
 * game is fully playable and simply has no family to form.
 *
 * The rules a client must not be trusted with — room size, the unanimous map vote, the
 * no-teleport check, and who may be a family — all live on the server. This is the wire.
 */
import type { SpeciesId } from '../animals/species'

/** The deployed room worker. */
const DEFAULT_ENDPOINT = 'wss://animal-attack-rooms.whatshuang.workers.dev'
/** Position updates per second. The room rejects impossible jumps, not chatty clients. */
const MOVE_INTERVAL_MS = 66

export interface RemotePlayer {
  id: string
  name: string
  color: string
  species: SpeciesId
  x: number
  z: number
  yaw: number
}

export interface RoomEvents {
  onWelcome?(you: { id: string; color: string; name: string }, players: RemotePlayer[]): void
  onJoined?(player: RemotePlayer): void
  onLeft?(id: string): void
  onChat?(from: string, text: string, color: string): void
  onBiome?(biome: string): void
  onVoteStarted?(biome: string, by: string, needed: number): void
  onVoteProgress?(accepted: number, needed: number): void
  onFamily?(withName: string, ok: boolean, reason?: string): void
  onStatus?(status: 'connecting' | 'open' | 'closed'): void
}

/**
 * Ask how busy a room is before joining it. Kate has nobody to play with unless family
 * happen to be in at the same time, so knowing whether anyone is there is the difference
 * between joining and giving up.
 */
export async function roomPopulation(
  room = 'kate',
  endpoint = DEFAULT_ENDPOINT,
): Promise<{ players: number; max: number; biome: string } | null> {
  const http = endpoint.replace(/^ws/, 'http')
  // Retried once: the very first request to a room that has never existed has to cold
  // start its Durable Object, and that occasionally times out. One retry turned a
  // one-in-five failure into none.
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const response = await fetch(`${http}/?room=${encodeURIComponent(room)}`, {
        signal: AbortSignal.timeout(6000),
      })
      if (response.ok) {
        return (await response.json()) as { players: number; max: number; biome: string }
      }
    } catch {
      // Fall through to the retry.
    }
    if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 600))
  }
  return null
}

export class RoomClient {
  private socket: WebSocket | null = null
  private readonly events: RoomEvents
  private readonly endpoint: string
  /** Everyone else in the room, by id. */
  readonly players = new Map<string, RemotePlayer>()
  selfId: string | null = null
  selfColor = '#4a9df8'
  /** The player you are in a family with, if any. */
  familyWith: string | null = null

  private lastSent = 0

  constructor(events: RoomEvents, endpoint = DEFAULT_ENDPOINT) {
    this.events = events
    this.endpoint = endpoint
  }

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN
  }

  connect(room = 'default'): void {
    if (this.socket) return
    this.events.onStatus?.('connecting')
    const socket = new WebSocket(`${this.endpoint}/?room=${encodeURIComponent(room)}`)
    this.socket = socket

    socket.addEventListener('open', () => this.events.onStatus?.('open'))
    socket.addEventListener('error', () => this.events.onStatus?.('closed'))
    socket.addEventListener('close', () => {
      this.socket = null
      this.players.clear()
      this.familyWith = null
      this.events.onStatus?.('closed')
    })
    socket.addEventListener('message', (event) => this.handle(String(event.data)))
  }

  disconnect(): void {
    this.socket?.close()
    this.socket = null
  }

  private handle(raw: string): void {
    let message: Record<string, unknown>
    try {
      message = JSON.parse(raw) as Record<string, unknown>
    } catch {
      return
    }

    switch (message.type) {
      case 'welcome': {
        const you = message.you as { id: string; color: string; name: string }
        this.selfId = you.id
        this.selfColor = you.color
        const players = (message.players ?? []) as RemotePlayer[]
        this.players.clear()
        for (const player of players) this.players.set(player.id, player)
        this.events.onWelcome?.(you, players)
        return
      }
      case 'joined': {
        const player = message.player as RemotePlayer
        this.players.set(player.id, player)
        this.events.onJoined?.(player)
        return
      }
      case 'left': {
        const id = String(message.id)
        this.players.delete(id)
        if (this.familyWith === id) this.familyWith = null
        this.events.onLeft?.(id)
        return
      }
      case 'moved': {
        const player = this.players.get(String(message.id))
        if (player) {
          player.x = Number(message.x)
          player.z = Number(message.z)
          player.yaw = Number(message.yaw)
        }
        return
      }
      case 'morphed': {
        const player = this.players.get(String(message.id))
        if (player) player.species = message.species as SpeciesId
        return
      }
      case 'renamed': {
        const player = this.players.get(String(message.id))
        if (player) player.name = String(message.name)
        return
      }
      case 'chat':
        this.events.onChat?.(String(message.from), String(message.text), String(message.color))
        return
      case 'biome-changed':
        this.events.onBiome?.(String(message.biome))
        return
      case 'vote-started':
        this.events.onVoteStarted?.(String(message.biome), String(message.by), Number(message.needed))
        return
      case 'vote-progress':
        this.events.onVoteProgress?.(Number(message.accepted), Number(message.needed))
        return
      case 'family': {
        const ok = Boolean(message.ok)
        if (ok) this.familyWith = String(message.withId)
        this.events.onFamily?.(String(message.withName), ok, message.reason as string | undefined)
        return
      }
      case 'family-ended':
        this.familyWith = null
        return
    }
  }

  private send(payload: Record<string, unknown>): void {
    if (!this.connected) return
    this.socket!.send(JSON.stringify(payload))
  }

  /** Throttled, so a 60Hz loop doesn't flood the room. */
  move(x: number, z: number, yaw: number, nowMs: number): void {
    if (nowMs - this.lastSent < MOVE_INTERVAL_MS) return
    this.lastSent = nowMs
    this.send({ type: 'move', x, z, yaw })
  }

  chat(text: string): void {
    this.send({ type: 'chat', text })
  }

  setName(name: string): void {
    this.send({ type: 'name', name })
  }

  setSpecies(species: SpeciesId): void {
    this.send({ type: 'species', species })
  }

  voteBiome(biome: string): void {
    this.send({ type: 'vote-biome', biome })
  }

  acceptVote(): void {
    this.send({ type: 'vote-accept' })
  }

  /** Ask a specific other player to be a family. Both sides must ask. */
  requestFamily(withId: string): void {
    this.send({ type: 'family-request', withId })
  }

  leaveFamily(): void {
    this.send({ type: 'family-leave' })
  }
}
